import type Phaser from "phaser";
import type { RectMask } from "@yjsgames/phaser-ui";
import { emitEnemyShipDestructionFx } from "../objects/enemyShipDestructionFx.js";
import type { FxImagePool } from "../objects/fxImagePool.js";
import { PALETTE_NUM } from "../themes/palette.js";

const PLAYER_SHIP_DESTRUCTION_FADE_MS = 300;

export interface PlayerShipDestructionFx {
  play(): void;
  restore(): void;
}

function createLocalFxImagePool(scene: Phaser.Scene): FxImagePool {
  const idle: Phaser.GameObjects.Image[] = [];
  return {
    acquire: () => idle.pop() ?? scene.add.image(0, 0, "hit_spark_white").setActive(false).setVisible(false),
    release: (img) => {
      scene.tweens.killTweensOf(img);
      img.clearMask();
      img.setBlendMode("NORMAL");
      img.setActive(false).setVisible(false);
      idle.push(img);
    },
  } as FxImagePool;
}

export function createPlayerShipDestructionFx(
  scene: Phaser.Scene,
  ship: Phaser.GameObjects.Image,
  gameviewMask: RectMask,
  fallbackSize: number,
): PlayerShipDestructionFx {
  const pool = createLocalFxImagePool(scene);
  let destroyed = false;
  return {
    play: () => {
      if (destroyed) return;
      destroyed = true;
      const shipCenterY = ship.y - ship.displayHeight / 2;
      const tint = ship.tintTopLeft || PALETTE_NUM.infoBlue;
      for (const fx of emitEnemyShipDestructionFx(scene, pool, {
        x: ship.x,
        y: shipCenterY,
        displayWidth: ship.displayWidth || fallbackSize,
        displayHeight: ship.displayHeight || fallbackSize,
        tint,
        isCrit: true,
      })) gameviewMask.apply(fx);
      scene.tweens.killTweensOf(ship);
      scene.tweens.add({
        targets: ship,
        alpha: 0,
        scale: 1.18,
        rotation: -0.22,
        duration: PLAYER_SHIP_DESTRUCTION_FADE_MS,
        ease: "Quad.easeOut",
        onComplete: () => ship.setVisible(false),
      });
    },
    restore: () => {
      destroyed = false;
      scene.tweens.killTweensOf(ship);
      ship.setVisible(true).setAlpha(1).setScale(1).setDisplaySize(fallbackSize, fallbackSize).setRotation(0);
    },
  };
}
